import React, { useEffect, useState, useCallback } from 'react';
import './TopSlider.css';

// Simple top slider component similar to BottomSlider
const TopSlider = ({ open, onClose, children, showGrabHandle = false, height = '50vh' }) => {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  // Determine CSS class based on height prop
  const getPanelClass = () => {
    let baseClass = 'top-slider-panel';
    if (height === '50vh' || height === '30vh') {
      baseClass += ' half-height';
    } else if (height === 'auto') {
      baseClass += ' auto-height';
    }
    return baseClass;
  };

  // Mount when opening
  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
    }
  }, [open, mounted]);

  // Handle animation end
  const handleAnimationEnd = useCallback(() => {
    if (closing && !open) {
      setMounted(false);
      if (onClose) {
        onClose();
      }
    }
  }, [closing, open, onClose]);

  // Handle close click
  const handleCloseClick = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (closing) return;
    
    // Call onClose immediately instead of just setting closing state
    if (onClose) {
      onClose();
    }
    setClosing(true);
  }, [closing, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.target === e.currentTarget) {
      handleCloseClick(e);
    }
  }, [handleCloseClick]);

  // Stop propagation on panel click
  const handlePanelClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  if (!mounted) return null;

  return (
    <div 
      className={`top-slider-root ${open && !closing ? 'open' : ''} ${closing ? 'closing' : ''}`}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="top-slider-backdrop"
        onClick={handleBackdropClick}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      />
      <div
        className={getPanelClass()}
        onClick={handlePanelClick}
        onAnimationEnd={handleAnimationEnd}
        onTransitionEnd={handleAnimationEnd}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {showGrabHandle && (
          <div className="top-slider-grabber" role="button" aria-label="Visual handle" />
        )}
        <button 
          className="top-slider-close" 
          onClick={handleCloseClick}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          aria-label="Close"
          type="button"
        >
          ×
        </button>
        <div 
          className="top-slider-content"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseMove={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default TopSlider;
